if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getErrorMessage(response) {
        // Handle 422 Unprocessable Entity (Business Logic Errors)
        if (response.status === 422) {
          const description = response.description || '';
          
          // Inventory/Quantity errors
          if (description.toLowerCase().includes('inventory') || description.toLowerCase().includes('quantity')) {
             const quantityInput = this.form.querySelector('[name="quantity"]');
             const quantity = quantityInput ? quantityInput.value : 'this item';
             return window.cartStrings.quantityError.replace('[quantity]', quantity);
          }
          
          // Use the description from Shopify if it's user-friendly enough
          return description;
        }

        // Handle 400 Bad Request (Validation Errors)
        if (response.status === 400) {
          return 'Please select all available options before adding to cart.';
        }

        // Handle 401 Unauthorized (Session Issues)
        if (response.status === 401) {
           return 'Your session has expired. Please refresh the page and try again.';
        }
        
        // Handle 404 Not Found
        if (response.status === 404) {
          return 'This product variant is no longer available.';
        }

        // Generic Fallback
        return response.message || response.description || window.cartStrings.error;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        
        // Check if the clicked button was the Checkout button
        if (evt.submitter && evt.submitter.name === 'checkout') {
          // If it's the checkout button, let it submit normally to /cart/add
          // But we need to intercept the response to redirect to checkout
          const formData = new FormData(this.form);
          
          // Disable buttons
          this.handleErrorMessage(); 
          this.submitButton.setAttribute('disabled', true);
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner').classList.remove('hidden');

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          // Add to cart first
          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                // Handle errors
                const errorMessage = this.getErrorMessage(response);
                this.handleErrorMessage(errorMessage);
                this.error = true;
                
                // Reset button state
                this.submitButton.classList.remove('loading');
                this.submitButton.removeAttribute('disabled');
                this.querySelector('.loading__spinner').classList.add('hidden');
                return;
              }

              // Success! Now redirect to checkout
              window.location.href = '/checkout';
            })
            .catch((e) => {
              console.error(e);
            });
            
          return;
        }

        // Standard Add to Cart Logic (AJAX)
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Capture original text to restore later (handles custom button text like "Pre-order")
        const originalButtonText = this.submitButtonText.textContent;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading', 'loading--text');
        this.submitButtonText.textContent = window.variantStrings.addingToCart;
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Ensure fetchConfig is available
        const fetchConfigFn = typeof fetchConfig === 'undefined' ? (type = 'json', method = 'POST') => {
          return {
            method,
            headers: {
              'Content-Type': 'application/json',
              'Accept': `application/${type}`,
              'X-Requested-With': 'XMLHttpRequest'
            },
          };
        } : fetchConfig;

        const config = fetchConfigFn('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Ensure fetchWithRetry is available (loaded from fetch-utils.js)
        const fetchWithRetryFn = typeof fetchWithRetry === 'undefined' ? (url, options) => fetch(url, options) : fetchWithRetry;

        fetchWithRetryFn(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const errorMessage = this.getErrorMessage(response);
              
              // Log detailed technical error for debugging
              console.error('[Cart Error]', {
                status: response.status,
                message: response.message,
                description: response.description,
                errors: response.errors
              });

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: errorMessage,
              });
              
              this.handleErrorMessage(errorMessage);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings.error || 'There was an error adding the product to your cart.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading', 'loading--text');
            this.submitButtonText.textContent = originalButtonText;
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
